// core/services/videoFeedService.ts — the Videos page on real posts (2026-09-26).
// Server: backend/apps/media/video_feed.py. Every list is a list of full Posts (each with
// `video`), so cards, the watch page and post actions all read one shape.

import { api } from '@/core/lib/apiClient';
import type { Post, VideoCategoryId, WatchProgress } from '@/core/models/post';

/** Why the hero video was picked — shown to the viewer (owner: "a visible why line"). */
export type FeaturedReason = 'rising' | 'trending' | 'newest';

export interface VideosHome {
  /** False until any member has posted a video — the page then shows labelled samples. */
  hasVideos: boolean;
  featured: { post: Post; reason: FeaturedReason } | null;
  trending: Post[];
  shorts: Post[];
  latest: Post[];
}

export type VideoFilter = 'all' | 'following' | 'trending' | 'shorts' | VideoCategoryId;

/** The watch page's Up next tabs (1.27.50). */
export type UpNextMode = 'for_you' | 'creator' | 'related';

export const videoFeedService = {
  home(): Promise<VideosHome> {
    return api<VideosHome>('/videos/home/');
  },

  async list(filter: VideoFilter, opts: { before?: string; limit?: number } = {}): Promise<Post[]> {
    const q = new URLSearchParams({ filter });
    if (opts.before) q.set('before', opts.before);
    if (opts.limit) q.set('limit', String(opts.limit));
    const res = await api<{ results: Post[] }>(`/videos/?${q.toString()}`);
    return res.results;
  },

  async upNext(postId: string, mode: UpNextMode = 'for_you'): Promise<Post[]> {
    const q = new URLSearchParams({ post: postId });
    if (mode !== 'for_you') q.set('mode', mode);
    const res = await api<{ results: Post[] }>(`/videos/up-next/?${q.toString()}`);
    return res.results;
  },

  /** "Don't recommend this creator" — their videos stop being recommended to you. */
  /** 1.27.60: save your place (`watched` marks it watched). Signed-in only. */
  async saveProgress(
    videoId: string,
    positionMs: number,
    watched = false,
  ): Promise<WatchProgress | null> {
    const res = await api<{ progress: WatchProgress | null }>(`/videos/${videoId}/progress/`, {
      method: 'PUT',
      body: { positionMs: Math.max(0, Math.round(positionMs)), ...(watched ? { watched } : {}) },
    });
    return res.progress;
  },

  /** 1.27.60: Mark as unwatched — forgets your place (views already counted stay counted). */
  async clearProgress(videoId: string): Promise<void> {
    await api(`/videos/${videoId}/progress/`, { method: 'DELETE' });
  },

  async hideCreator(handle: string): Promise<void> {
    await api('/videos/hidden-creators/', { method: 'POST', body: { handle } });
  },

  async unhideCreator(handle: string): Promise<void> {
    await api(`/videos/hidden-creators/?handle=${encodeURIComponent(handle)}`, {
      method: 'DELETE',
    });
  },

  /** Report 3 s of watching. Idempotent per person per day server-side. */
  async view(videoId: string): Promise<number | null> {
    try {
      const res = await api<{ views: number }>(`/videos/${videoId}/view/`, { method: 'POST' });
      return res.views;
    } catch {
      return null; // a missed count never interrupts playback
    }
  },

  async guessCategory(text: string): Promise<VideoCategoryId | null> {
    try {
      const res = await api<{ category: VideoCategoryId | null }>('/videos/category-guess/', {
        method: 'POST',
        body: { text },
      });
      return res.category;
    } catch {
      return null;
    }
  },

  async watchLater(): Promise<Post[]> {
    const res = await api<{ results: Post[] }>('/videos/watch-later/');
    return res.results;
  },

  async saveForLater(postId: string): Promise<void> {
    await api('/videos/watch-later/', { method: 'POST', body: { postId } });
  },

  async removeFromLater(postId: string): Promise<void> {
    await api(`/videos/watch-later/?postId=${encodeURIComponent(postId)}`, { method: 'DELETE' });
  },
} as const;
